import type { ExitPendingApprovalRow, MyExit, ResignationReasonOption } from "./types";
import { formatExitDateDisplay } from "./exitDateUtils";

const EXIT_STATUS_LABELS: Record<string, string> = {
  LINKED_TERMINATION_CANCELLED: "Termination cancelled",
  FINAL_SETTLEMENT: "Final settlement",
  EXIT_COMPLETED: "Exit completed",
  HR_REVIEW: "HR review",
  MANAGER_APPROVAL: "Manager approval",
  MANAGER_APPROVED: "Manager approved",
  NOTICE_PERIOD: "Notice period",
  EXIT_IN_PROGRESS: "Exit in progress",
  REJECTED: "Rejected",
  WITHDRAWN: "Withdrawn",
  SUBMITTED: "Submitted",
};

function normalizeStatus(status: string | null | undefined): string {
  return String(status ?? "").trim();
}

function titleCaseStatus(status: string): string {
  return status
    .replace(/_/g, " ")
    .trim()
    .split(/\s+/)
    .map((word) => {
      const upper = word.toUpperCase();
      if (["HR", "API", "ID", "LWD", "ESS"].includes(upper)) {
        return upper;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

export function humanizeExitStatusLabel(status: string | null | undefined): string {
  const raw = normalizeStatus(status);
  if (!raw) return "—";
  const key = raw.toUpperCase();
  if (EXIT_STATUS_LABELS[key]) return EXIT_STATUS_LABELS[key];
  if (raw.includes("_") || raw === raw.toUpperCase()) {
    return titleCaseStatus(raw);
  }
  return raw;
}

export function asExitRecord(exit: MyExit): Record<string, unknown> {
  return exit as Record<string, unknown>;
}

/** Normalized exit lifecycle / workflow phase (uppercase) for filters and badges. */
export function exitWorkflowPhaseKey(
  row: MyExit | ExitPendingApprovalRow,
): string {
  const raw =
    row.exitStatus ??
    (typeof row.status === "string" ? row.status : undefined) ??
    row.workflow?.workflowStatus;
  return String(raw ?? "").toUpperCase();
}

/** Table “Status” column: human label when API sends workflow snapshot */
export function exitListStatusLabel(exit: MyExit): string {
  const label = exit.workflow?.workflowStatusLabel;
  if (typeof label === "string" && label.trim()) {
    return humanizeExitStatusLabel(label);
  }
  const phase = exit.exitStatus ?? exit.status;
  if (phase !== undefined && phase !== null && String(phase).length > 0) {
    return humanizeExitStatusLabel(String(phase));
  }
  return "—";
}

/** Label for a resignation reason option (same master list as the resignation form). */
export function resignationReasonOptionLabel(opt: ResignationReasonOption): string {
  const r = opt as unknown as Record<string, unknown>;
  const candidates: unknown[] = [
    opt.label,
    opt.displayName,
    opt.name,
    opt.reasonText,
    opt.title,
    r.display_name,
    r.reason_text,
  ];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return opt.id;
}

export function reasonDisplay(exit: MyExit): string {
  if (typeof exit.reasonText === "string" && exit.reasonText.trim()) {
    return exit.reasonText.trim();
  }
  const dn = exit.reason?.displayName;
  if (typeof dn === "string" && dn.trim()) return dn.trim();
  const r = asExitRecord(exit);
  if (typeof r.reasonLabel === "string") return r.reasonLabel;
  if (typeof r.reasonName === "string") return r.reasonName;
  if (exit.reasonId) return exit.reasonId;
  return "—";
}

export function lastWorkingDisplay(exit: MyExit): string {
  const r = asExitRecord(exit);
  const approved =
    typeof r.lastWorkingDayApproved === "string"
      ? r.lastWorkingDayApproved
      : undefined;
  const requested =
    typeof r.lastWorkingDayRequested === "string"
      ? r.lastWorkingDayRequested
      : undefined;
  const raw =
    approved ||
    requested ||
    exit.lastWorkingDayApproved ||
    exit.lastWorkingDayRequested;
  return typeof raw === "string" && raw ? formatExitDateDisplay(raw) : "—";
}

export function submittedOnDisplay(exit: MyExit): string {
  return formatExitDateDisplay(exit.createdAt);
}

function pickNonEmptyString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function nameFromPopulatedManager(v: unknown): string | undefined {
  if (!v || typeof v !== "object") return undefined;
  const o = v as Record<string, unknown>;
  const full = pickNonEmptyString(o, ["fullName", "name", "displayName"]);
  if (full) return full;
  const fn = typeof o.firstName === "string" ? o.firstName.trim() : "";
  const ln = typeof o.lastName === "string" ? o.lastName.trim() : "";
  if (fn && ln) return `${fn} ${ln}`;
  if (fn) return fn;
  if (ln) return ln;
  return undefined;
}

/**
 * Functional manager label for my-exit / resignation UI.
 * Backend DTOs may use `functionalManagerName`, a string `functionalManager`, a populated user object, or snake_case.
 */
export function functionalManagerDisplay(exit: MyExit): string {
  const r = asExitRecord(exit);
  const emp = r.employee;
  if (emp && typeof emp === "object") {
    const e = emp as Record<string, unknown>;
    const direct = pickNonEmptyString(e, [
      "functionalManagerName",
      "functional_manager_name",
      "functionalManagerFullName",
    ]);
    if (direct) return direct;

    const fmRaw = e.functionalManager ?? e.functional_manager;
    if (typeof fmRaw === "string" && fmRaw.trim()) return fmRaw.trim();
    const fromObj = nameFromPopulatedManager(fmRaw);
    if (fromObj) return fromObj;

    const userNested = e.user;
    if (userNested && typeof userNested === "object") {
      const u = userNested as Record<string, unknown>;
      const fromUser = pickNonEmptyString(u, [
        "functionalManagerName",
        "functional_manager_name",
      ]);
      if (fromUser) return fromUser;
      const ufm = u.functionalManager ?? u.functional_manager;
      if (typeof ufm === "string" && ufm.trim()) return ufm.trim();
      const uObj = nameFromPopulatedManager(ufm);
      if (uObj) return uObj;
    }
  }

  const root = pickNonEmptyString(r, [
    "functionalManagerName",
    "functional_manager_name",
  ]);
  if (root) return root;
  const fmRoot = r.functionalManager ?? r.functional_manager;
  if (typeof fmRoot === "string" && fmRoot.trim()) return fmRoot.trim();
  return nameFromPopulatedManager(fmRoot) ?? "—";
}
